import { type Static, Type } from "@sinclair/typebox"
import { AlarmRuleProjectionSchema } from "./alarm-rules"
import {
  CursorQuerySchema,
  createPageSchema,
  IsoDateTimeSchema,
  JsonRecordSchema,
  Nullable,
} from "./common"

// Flota — contratos de vehículos, sus devices (imei+nodo) y grupos. La fuente de verdad
// vive en seguridad; el gateway proyecta solo los vehículos cuyos devices aloja.

// Un device del vehículo: ata un IMEI al NODO (servidor) donde vive en el gateway.
export const VehicleDeviceSchema = Type.Object({
  imei: Type.String(),
  node_id: Type.String(),
  name: Nullable(Type.String()),
})
export type VehicleDevice = Static<typeof VehicleDeviceSchema>

export const VehicleSchema = Type.Object({
  id: Type.String(),
  domain_id: Type.String(),
  plate: Nullable(Type.String()),
  plate_norm: Nullable(Type.String()),
  name: Type.String(),
  code: Nullable(Type.String()),
  brand: Nullable(Type.String()),
  color: Nullable(Type.String()),
  model: Nullable(Type.String()),
  year: Nullable(Type.Integer()),
  type: Type.String(),
  serial: Nullable(Type.String()),
  engine_number: Nullable(Type.String()),
  chassis_number: Nullable(Type.String()),
  end_client: Nullable(Type.String()),
  comments: Nullable(Type.String()),
  primary_device_imei: Nullable(Type.String()),
  metadata: JsonRecordSchema,
  active: Type.Boolean(),
  devices: Type.Array(VehicleDeviceSchema),
  group_ids: Type.Array(Type.String()),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type Vehicle = Static<typeof VehicleSchema>

export const VehiclePageSchema = createPageSchema(VehicleSchema)
export type VehiclePage = Static<typeof VehiclePageSchema>

export const ListVehiclesQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    // Solo el admin de instancia acota por dominio; el maestro siempre ve el suyo.
    domain: Type.Optional(Type.String()),
    q: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
    // Filtro para la proyección del gateway: vehículos con algún device en este nodo.
    node_id: Type.Optional(Type.String()),
    group_id: Type.Optional(Type.String()),
    // Solo los vehículos SIN ningún equipo (tablero de desconexión del app). Son pocos
    // —282 de 4.997 en la flota más grande, oct-2026—: filtrarlos aquí evita que el app
    // recorra la flota entera para encontrarlos.
    without_devices: Type.Optional(Type.Boolean()),
  }),
])
export type ListVehiclesQuery = Static<typeof ListVehiclesQuerySchema>

const DeviceInputSchema = Type.Object({
  imei: Type.String({ minLength: 10, maxLength: 20 }),
  node_id: Type.String({ minLength: 1 }),
  name: Type.Optional(Nullable(Type.String())),
})

export const CreateVehicleRequestSchema = Type.Object({
  // Solo el admin de instancia puede fijar dominio; el maestro usa el suyo (server-side).
  domain_id: Type.Optional(Type.String()),
  name: Type.String({ minLength: 1 }),
  plate: Type.Optional(Nullable(Type.String())),
  code: Type.Optional(Nullable(Type.String())),
  brand: Type.Optional(Nullable(Type.String())),
  color: Type.Optional(Nullable(Type.String())),
  model: Type.Optional(Nullable(Type.String())),
  year: Type.Optional(Nullable(Type.Integer())),
  type: Type.Optional(Type.String()),
  serial: Type.Optional(Nullable(Type.String())),
  engine_number: Type.Optional(Nullable(Type.String())),
  chassis_number: Type.Optional(Nullable(Type.String())),
  end_client: Type.Optional(Nullable(Type.String())),
  comments: Type.Optional(Nullable(Type.String({ maxLength: 1000 }))),
  primary_device_imei: Type.Optional(Nullable(Type.String())),
  metadata: Type.Optional(JsonRecordSchema),
  devices: Type.Optional(Type.Array(DeviceInputSchema)),
  group_ids: Type.Optional(Type.Array(Type.String())),
})
export type CreateVehicleRequest = Static<typeof CreateVehicleRequestSchema>

export const UpdateVehicleRequestSchema = Type.Object({
  /**
   * Mueve el vehículo a OTRO tenant (id `dom_…` o slug). Exige alcance
   * cross-tenant: un actor atado a su dominio no puede sacar un vehículo del
   * suyo ni meterlo en otro. Al mover se limpia lo que pertenece al tenant
   * anterior (grupos y objetivos de reglas aquí; talleres, proyectos,
   * conductores y servicios en el gateway); eventos e historial se conservan,
   * porque van por IMEI y no por vehículo.
   */
  domain_id: Type.Optional(Type.String()),
  name: Type.Optional(Type.String({ minLength: 1 })),
  plate: Type.Optional(Nullable(Type.String())),
  code: Type.Optional(Nullable(Type.String())),
  brand: Type.Optional(Nullable(Type.String())),
  color: Type.Optional(Nullable(Type.String())),
  model: Type.Optional(Nullable(Type.String())),
  year: Type.Optional(Nullable(Type.Integer())),
  type: Type.Optional(Type.String()),
  serial: Type.Optional(Nullable(Type.String())),
  engine_number: Type.Optional(Nullable(Type.String())),
  chassis_number: Type.Optional(Nullable(Type.String())),
  end_client: Type.Optional(Nullable(Type.String())),
  comments: Type.Optional(Nullable(Type.String({ maxLength: 1000 }))),
  primary_device_imei: Type.Optional(Nullable(Type.String())),
  metadata: Type.Optional(JsonRecordSchema),
  active: Type.Optional(Type.Boolean()),
  group_ids: Type.Optional(Type.Array(Type.String())),
})
export type UpdateVehicleRequest = Static<typeof UpdateVehicleRequestSchema>

// PUT /:id/devices — reemplaza el conjunto completo de devices del vehículo.
export const SetVehicleDevicesRequestSchema = Type.Object({
  devices: Type.Array(DeviceInputSchema),
})
export type SetVehicleDevicesRequest = Static<typeof SetVehicleDevicesRequestSchema>

// POST /:id/devices — agrega (o MUEVE) un device suelto al vehículo. Lo usa el proxy
// de asignación del panel del gateway.
export const AddVehicleDeviceRequestSchema = DeviceInputSchema
export type AddVehicleDeviceRequest = Static<typeof AddVehicleDeviceRequestSchema>

// --- Grupos de vehículos -----------------------------------------------------

export const VehicleGroupSchema = Type.Object({
  id: Type.String(),
  domain_id: Type.String(),
  name: Type.String(),
  description: Nullable(Type.String()),
  color: Nullable(Type.String()),
  vehicle_count: Type.Optional(Type.Integer({ minimum: 0 })),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type VehicleGroup = Static<typeof VehicleGroupSchema>

export const VehicleGroupPageSchema = createPageSchema(VehicleGroupSchema)
export type VehicleGroupPage = Static<typeof VehicleGroupPageSchema>

export const ListVehicleGroupsQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    domain: Type.Optional(Type.String()),
    q: Type.Optional(Type.String()),
  }),
])
export type ListVehicleGroupsQuery = Static<typeof ListVehicleGroupsQuerySchema>

export const CreateVehicleGroupRequestSchema = Type.Object({
  domain_id: Type.Optional(Type.String()),
  name: Type.String({ minLength: 1 }),
  description: Type.Optional(Nullable(Type.String())),
  color: Type.Optional(Nullable(Type.String())),
})
export type CreateVehicleGroupRequest = Static<typeof CreateVehicleGroupRequestSchema>

export const UpdateVehicleGroupRequestSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1 })),
  description: Type.Optional(Nullable(Type.String())),
  color: Type.Optional(Nullable(Type.String())),
})
export type UpdateVehicleGroupRequest = Static<typeof UpdateVehicleGroupRequestSchema>

// Membresía de grupo (N:M). El app la usa para asignar/quitar vehículos a un grupo.
export const AddGroupVehiclesRequestSchema = Type.Object({
  vehicle_ids: Type.Array(Type.String(), { minItems: 1 }),
})
export type AddGroupVehiclesRequest = Static<typeof AddGroupVehiclesRequestSchema>

// --- Proyección para el gateway (M2M, node-scoped) ---------------------------
// El worker de reconcile del gateway jala esto y upserta su proyección local. El
// `gateway_vehicle_id` (de metadata) es la llave estable: el gateway conserva sus
// ids para no romper FKs de taller/proyecto/conductor/servicio. `domain_slug` = el
// `tenant_id` del gateway. `group_ids` son ids de auth (el gateway adopta esos ids
// para grupos, que solo tienen membresías y se reconstruyen sin riesgo).

export const VehicleProjectionEntrySchema = Type.Object({
  id: Type.String(),
  gateway_vehicle_id: Nullable(Type.String()),
  domain_slug: Type.String(),
  plate: Nullable(Type.String()),
  plate_norm: Nullable(Type.String()),
  name: Type.String(),
  code: Nullable(Type.String()),
  brand: Nullable(Type.String()),
  color: Nullable(Type.String()),
  model: Nullable(Type.String()),
  year: Nullable(Type.Integer()),
  type: Type.String(),
  serial: Nullable(Type.String()),
  engine_number: Nullable(Type.String()),
  chassis_number: Nullable(Type.String()),
  primary_device_imei: Nullable(Type.String()),
  active: Type.Boolean(),
  // Devices de ESTE nodo (lo que aloja este gateway).
  devices: Type.Array(Type.Object({ imei: Type.String(), node_id: Type.String() })),
  group_ids: Type.Array(Type.String()),
})
export type VehicleProjectionEntry = Static<typeof VehicleProjectionEntrySchema>

export const VehicleProjectionGroupSchema = Type.Object({
  id: Type.String(),
  domain_slug: Type.String(),
  name: Type.String(),
  color: Nullable(Type.String()),
})
export type VehicleProjectionGroup = Static<typeof VehicleProjectionGroupSchema>

// Jaula de un usuario con acceso RESTRINGIDO, ya acotada a este nodo: los ids de
// auth de sus vehículos con device aquí y los IMEIs de esos devices. Un usuario
// restringido SIN nada en este nodo también viaja (listas vacías): el nodo debe
// cerrar, no abrir. El gateway traduce `vehicle_ids` a su espacio de ids con el
// `gateway_vehicle_id` de `vehicles[]`.
export const UserAccessProjectionSchema = Type.Object({
  user_id: Type.String(),
  domain_slug: Type.String(),
  /** Su acceso por vehículo le da TODA la flota (gana sobre la jaula; zonas y personal no). */
  all_vehicles: Type.Boolean(),
  vehicle_ids: Type.Array(Type.String()),
  imeis: Type.Array(Type.String()),
})
export type UserAccessProjection = Static<typeof UserAccessProjectionSchema>

/**
 * Catálogo de TENANTS para el nodo. Cada nodo guarda su propia tabla `iotgw.tenants`
 * y hasta sep-2026 solo se llenaba al crear el tenant desde el panel (o a mano al
 * montar el nodo): un tenant nuevo existía en UN nodo y en los demás la API respondía
 * «tenant_id no existe» — sin datos, sin alarmas y sin poder configurarlas (casos
 * `evflit`, `daviviendamango`, `bcrmango`). Viaja en la proyección que el nodo ya pide
 * cada 60 s: sin endpoint, worker, scope ni token nuevos.
 */
export const DomainProjectionSchema = Type.Object({
  slug: Type.String(),
  name: Type.String(),
  /** `active` en el nodo = el dominio no está suspendido. */
  active: Type.Boolean(),
})
export type DomainProjection = Static<typeof DomainProjectionSchema>

export const VehicleProjectionSchema = Type.Object({
  node_id: Type.String(),
  /** Todos los dominios de la instancia menos `system` (ver DomainProjectionSchema). */
  domains: Type.Array(DomainProjectionSchema),
  vehicles: Type.Array(VehicleProjectionEntrySchema),
  groups: Type.Array(VehicleProjectionGroupSchema),
  user_access: Type.Array(UserAccessProjectionSchema),
  // Reglas de alarma por vehículo que aplican a los devices de ESTE nodo, con la
  // precedencia ya resuelta (ver alarm-rule-service). Viajan en la misma proyección
  // para no añadir otro endpoint, otro worker y otro token M2M por mantener.
  rules: Type.Array(AlarmRuleProjectionSchema),
  // `jti` de los tokens de acceso personal vigentes (no revocados ni caducados, con
  // usuario y dominio activos). El nodo verifica los JWT en local: solo acepta un PAT
  // que esté en esta lista. Opcional: un gateway anterior lo ignora.
  active_tokens: Type.Optional(Type.Array(Type.String())),
})
export type VehicleProjection = Static<typeof VehicleProjectionSchema>

export const VehicleProjectionQuerySchema = Type.Object({
  node_id: Type.String({ minLength: 1 }),
})
export type VehicleProjectionQuery = Static<typeof VehicleProjectionQuerySchema>
